import { useEffect, useState } from "react";
import { FaTerminal, FaPlus, FaArchive, FaStar, FaPen, FaEye } from "react-icons/fa";
import { SNIPPETS_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_SNIPPET_TARGET, OPTIONS_SNIPPET_KIND, OPTIONS_CODE_LANGUAGE, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { AppCodeBlock } from "../../../../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FormGrid, FieldInput, FieldSelect, FieldTextarea, FieldCode, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { useDateRange, onMatchesRange } from "../../../../_UI/useDateRange";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { SnippetReadModal } from "./SnippetReadModal";

const EMPTY_VALUES = {
    title: "", description: "", language: "bash", kind: "COMMAND",
    target: "GENERAL", workingDirectory: "", code: "", isFavorite: false,
};

export const PanelSnippets = ({ subProjectId }) => {

    const [useSearch, setSearch] = useState("");

    const DATE_RANGE = useDateRange();
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useSnippetEdit, setSnippetEdit] = useState(null);
    const [useSnippetRead, setSnippetRead] = useState(null);

    const { apiGet: onGetSnippets, useApiData: SNIPPETS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadSnippets = () => onGetSnippets({
        endpoint: SNIPPETS_CONSTANTS.BY_SUBPROJECT, isDetail: true, config: { pathParams: { subProjectId } },
    });

    useEffect(() => { onLoadSnippets(); }, [subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { title: { required: true }, code: { required: true } },

        onSubmit: async (values) => {

            const IS_EDIT = !!useSnippetEdit?.id;

            const PAYLOAD = {
                title: values.title.trim(),
                description: values.description || "",
                language: values.language, kind: values.kind, target: values.target,
                workingDirectory: values.workingDirectory || "",
                code: values.code,
                isFavorite: !!values.isFavorite,
            };

            if (!IS_EDIT) PAYLOAD.subProjectId = subProjectId;

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: SNIPPETS_CONSTANTS.UPDATE, successMessage: "Script actualizado", id: useSnippetEdit.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: SNIPPETS_CONSTANTS.CREATE, successMessage: "Script guardado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            mutateLocal(IS_EDIT
                ? { id: useSnippetEdit.id, patch: RESPONSE.data }
                : { insert: RESPONSE.data });

            FORM.reset();
            setSnippetEdit(null);
            setIsModalOpen(false);
        },
    });

    const onOpenCreate = () => {
        setSnippetEdit(null);
        FORM.setValues(EMPTY_VALUES);
        setIsModalOpen(true);
    };

    const onOpenEdit = (snippet) => {
        setSnippetEdit(snippet);
        FORM.setValues({
            title: snippet.title ?? "",
            description: snippet.description ?? "",
            language: snippet.language ?? "bash",
            kind: snippet.kind ?? "COMMAND",
            target: snippet.target ?? "GENERAL",
            workingDirectory: snippet.workingDirectory ?? "",
            code: snippet.code ?? "",
            isFavorite: !!snippet.isFavorite,
        });
        setIsModalOpen(true);
    };

    const onCloseModal = () => {
        setIsModalOpen(false);
        setSnippetEdit(null);
    };

    const onToggleFavorite = async (snippet) => {
        const previous = !!snippet.isFavorite;
        const next = !previous;
        mutateLocal({ id: snippet.id, patch: { isFavorite: next } });

        const RESPONSE = await onSilentPatch({
            endpoint: SNIPPETS_CONSTANTS.UPDATE,
            id: snippet.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del script.",
        });

        if (!RESPONSE.success) mutateLocal({ id: snippet.id, patch: { isFavorite: previous } });
    };

    const onDelete = async (snippet) => {

        const IS_CONFIRMED = await confirm.danger("Archivar script", `Se archiva "${snippet.title}".`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: SNIPPETS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: snippet.id }, successMessage: "Script archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: snippet.id, remove: true });
    };

    const ITEMS = SNIPPETS ?? [];

    const VISIBLE = [...ITEMS].filter((snippet) => onMatchesRange(snippet, DATE_RANGE.useRange, "createdAt"))
        .filter((snippet) => onMatchesSearch(useSearch, [
        snippet.title, snippet.description, snippet.code, snippet.workingDirectory, snippet.target, snippet.language,
    ]))
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.title ?? "").localeCompare(String(b.title ?? "")));

    const PAGER = usePagedList(VISIBLE, 6, "scriptsPage");

    return (
        <>
            <div className="LStudio__app_panel_bar">


                <ViewToolbar
                    filters={
                        <SearchFilter value={useSearch} onChange={setSearch} total={ITEMS.length} matches={VISIBLE.length}
                            placeholder="Ej.: Deploy, npm run build o /scripts..." />
                    }
                    dateRange={DATE_RANGE}
                    total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Script" },
                        { key: "target", label: "Destino" },
                        { key: "language", label: "Lenguaje" },
                        { key: "workingDirectory", label: "Directorio" },
                        { key: "code", label: "Comando" },
                    ]}
                    exportRows={VISIBLE.map((snippet) => ({
                        title: snippet.title,
                        target: onFindOptionLabel(OPTIONS_SNIPPET_TARGET, snippet.target),
                        language: snippet.language,
                        workingDirectory: snippet.workingDirectory ?? "",
                        code: snippet.code,
                    }))}
                    exportName="scripts"
                    actions={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo script</AppButton>} />

            </div>

            {IS_LOADING && <Skeleton variant="box" height={150} />}

            {
                !IS_LOADING && VISIBLE.length === 0 && (
                    <EmptyState icon={<FaTerminal />} title="Sin scripts guardados"
                        description="Guardá los comandos para levantar el frontend, el backend o resetear la base." />
                )
            }

            {
                !IS_LOADING && VISIBLE.length > 0 && (
                    <div className="LStudio__app_snippets_list">
                        {
                            PAGER.paged.map((snippet) => (
                                <Card key={snippet.id} className="LStudio__app_snippets_item">

                                    <div className="LStudio__app_snippets_item_head">

                                        <span className="LStudio__app_snippets_item_title">{snippet.title}</span>

                                        <Badge tone="accent" size="sm">{onFindOptionLabel(OPTIONS_SNIPPET_TARGET, snippet.target)}</Badge>

                                        <Badge tone="neutral" size="sm">{onFindOptionLabel(OPTIONS_SNIPPET_KIND, snippet.kind)}</Badge>

                                    </div>

                                    {snippet.description && <p className="LStudio__app_snippets_item_desc">{snippet.description}</p>}

                                    {
                                        snippet.workingDirectory && (
                                            <p className="LStudio__app_snippets_item_dir">{snippet.workingDirectory}</p>
                                        )
                                    }

                                    <AppCodeBlock code={snippet.code} language={snippet.language} isPretty={false} maxLines={4} />

                                    <div className="LStudio__app_snippets_item_actions">

                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEye />}
                                            onClick={() => setSnippetRead(snippet)}>Ver</AppButton>

                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaStar />}
                                            onClick={() => onToggleFavorite(snippet)}>
                                            {snippet.isFavorite ? "Quitar destacado" : "Destacar"}
                                        </AppButton>

                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPen />}
                                            onClick={() => onOpenEdit(snippet)}>Editar</AppButton>

                                        <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                            onClick={() => onDelete(snippet)}>Archivar</AppButton>

                                    </div>

                                </Card>
                            ))
                        }
                    </div>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />


            <SnippetReadModal
                isOpen={!!useSnippetRead}
                snippet={useSnippetRead}
                onClose={() => setSnippetRead(null)}
            />

            <AppModal isOpen={useIsModalOpen} onClose={onCloseModal}
                isDirty={FORM.isDirty} size="lg"
                title={useSnippetEdit ? "Editar script" : "Nuevo script"}
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={onCloseModal}>Cancelar</AppButton>
                        <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                            {useSnippetEdit ? "Guardar cambios" : "Guardar"}
                        </AppButton>
                    </>
                } >

                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="snippet_isFavorite" label="Destacado" text="Mostrar este script primero"
                        isChecked={!!FORM.values.isFavorite}
                        onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
                </div>

                <FormGrid columns={2}>

                    <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: Levantar backend" />

                    <FieldTextarea {...FORM.field("description")} label="Descripción" rows={2} span={2} placeholder="Ej: Portal interno de operaciones" />

                    <FieldSelect {...FORM.field("target")} label="Destino" options={OPTIONS_SNIPPET_TARGET} isSearchable />

                    <FieldSelect {...FORM.field("kind")} label="Tipo" options={OPTIONS_SNIPPET_KIND} isSearchable />

                    <FieldSelect {...FORM.field("language")} label="Lenguaje" options={OPTIONS_CODE_LANGUAGE} isSearchable />

                    <FieldInput {...FORM.field("workingDirectory")} label="Directorio" placeholder="/home/dev/proyecto/api" />

                    <FieldCode {...FORM.field("code")} label="Comando" language={FORM.values.language}
                        rows={6} placeholder="npm run start:dev" span={2} />

                </FormGrid>

            </AppModal>
        </>
    );
};
